import React, { useState } from 'react';
import RatingSelector from '../../atoms/rating-selector';
import Input, { InputProps } from '../../atoms/input';
import Button from '../../atoms/button';
import { useFormat } from 'helpers/hooks/useFormat';
import ReviewSubmittedConfirmation, { ReviewSubmittedConfirmationProps } from './ReviewSubmittedConfirmation';
import Textarea from 'components/commercetools-ui/atoms/textarea';
import TermsAndConditions, {
  TermsAndConditionsProps,
} from 'components/commercetools-ui/molecules/terms-conditions/TermsAndConditions';

export type InputPick = Pick<InputProps, 'label' | 'placeholder'>;

export interface ReviewFormProps {
  onSubmit: (data: { rating: number; title: string; review: string; displayName: string }) => Promise<boolean>;
  selectorLabel?: InputPick; // overall rating
  reviewTitleLabel?: InputPick; // review title
  reviewLabel?: InputPick; // review
  displayNameLabel?: InputPick; // display name
  submitButtonLabel?: string; // submit button label

  termsOfUseLink: string; // Terms of Use
  privacyNoticeLink: string; // Privacy Notice

  reviewConfirmation?: ReviewSubmittedConfirmationProps;
  termsAndConditions?: TermsAndConditionsProps;
  onConfirmationChange?: (isConfirming: boolean) => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  onSubmit,
  selectorLabel,
  reviewTitleLabel,
  reviewLabel,
  displayNameLabel,
  submitButtonLabel,
  reviewConfirmation,
  termsAndConditions,
  onConfirmationChange,
}) => {
  const { formatMessage } = useFormat({ name: 'common' });
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [review, setReview] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [touched, setTouched] = useState<{ [k: string]: boolean }>({});
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const errors = {
    rating: rating < 1,
    title: !title.trim(),
    displayName: !displayName.trim(),
  };
  const hasError = Object.values(errors).some(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ rating: true, title: true, displayName: true });

    if (hasError) return;
    setSubmitting(true);
    const success = await onSubmit({ rating, title, review, displayName });
    setSubmitting(false);
    if (success) {
      setShowConfirmation(true);
      onConfirmationChange?.(true);
    }
  };

  if (showConfirmation && reviewConfirmation) {
    return <ReviewSubmittedConfirmation {...reviewConfirmation} />;
  }

  return (
    <form
      className="flex w-full max-w-[540px] flex-col gap-24 bg-white"
      onSubmit={handleSubmit}
      data-testid="review-form"
    >
      {selectorLabel?.label && (
        <div className="flex flex-col gap-12">
          <label className="body-text-medium font-medium" htmlFor="rating">
            {selectorLabel.label}*
          </label>

          <div>
            <RatingSelector
              value={rating}
              onChange={setRating}
              starProps={{ strokeVariant: 'gray' }}
              name="rating"
              ariaLabel="Rating"
            />
            {touched.rating && errors.rating && (
              <div className="body-text-small mt-1 text-feedback-error">
                {formatMessage({ id: 'overallRatingError', defaultMessage: 'Please select a rating.' })}
              </div>
            )}
          </div>
        </div>
      )}

      {reviewTitleLabel && (
        <Input
          id="review-title"
          label={reviewTitleLabel?.label || formatMessage({ id: 'reviewTitle', defaultMessage: 'Review Title' })}
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, title: true }))}
          placeholder={reviewTitleLabel?.placeholder}
          error={
            touched.title && errors.title
              ? formatMessage({ id: 'reviewTitleError', defaultMessage: 'Title is required.' })
              : ''
          }
        />
      )}

      {reviewLabel && (
        <Textarea
          id="review"
          label={reviewLabel?.label || formatMessage({ id: 'review', defaultMessage: 'Review' })}
          value={review}
          onChange={(e) => setReview(e.target.value)}
          placeholder={reviewLabel?.placeholder}
        />
      )}

      {displayNameLabel && (
        <Input
          id="display-name"
          label={displayNameLabel?.label}
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, displayName: true }))}
          placeholder={displayNameLabel?.placeholder}
          error={
            touched.displayName && errors.displayName
              ? formatMessage({ id: 'displayNameError', defaultMessage: 'Name is required.' })
              : ''
          }
        />
      )}

      {termsAndConditions && <TermsAndConditions {...termsAndConditions} />}

      <Button variant="primary" type="submit" className="mt-8" disabled={hasError || submitting} loading={submitting}>
        {submitButtonLabel}
      </Button>
    </form>
  );
};

export default ReviewForm;
